import { Grid, Typography } from '@mui/material';
import { ShimmerLine, VpcPeeringPanel } from '@hevoio/hevo-ui-toolkit';
import clsx from 'clsx';
import React from 'react';
import HdSwitchWrapper from '../../../../switch/components/switch-wrapper/HdSwitchWrapper';
import RequestSSHAccess from '../connector-reusable-options/RequestSSHAccess';
import { HdLink } from '../../../../components';
import { useSSHConnectionMode } from './hooks/useSSHConnectionMode';
import styles from './SSHConfig.module.scss';

interface SSHConfigProps {
  /** Display copy only — words the RequestSSHAccess banner ('MongoDB', 'database', …). */
  entityTypeIdentifier: string;
  /**
   * The real source/destination type hermes matches on (`AWS_RDS_POSTGRES`,
   * `REDSHIFT`, …), NOT the display string above. Omit it and no peering
   * lookup happens, so the form renders exactly as it does today.
   */
  entityType?: string;
  hideRequestSSHAccessComponent?: boolean;
  showNeedHelp?: boolean;
  onNeedHelpClick?: Function;
  children: React.ReactNode;
  className?: string;
  /** The customer's database host, for the panel's soft in-range hint. */
  databaseHost?: string;
  /**
   * Editing an existing entity. Peering only ever defaults itself on for a NEW
   * one: an edit form for a source that connects directly has the same empty
   * form values as a fresh one, so without this the default would change how a
   * working pipeline connects. Named to match the sibling PathPrefixConfig.
   */
  isEditing?: boolean;
}

/**
 * Stands in for one HdSwitchWrapper row while the peering spec resolves: the
 * switch, its label, and the sentence under it. Shaped like the row rather than
 * a flat line, so nothing moves when the real toggle replaces it.
 */
function ToggleRowShimmer({ dataId }: { dataId: string }) {
  return (
    <div className={styles.toggleRowShimmer} data-id={dataId}>
      <ShimmerLine className={styles.switchShimmer} />

      <div className={styles.toggleTextShimmer}>
        <ShimmerLine className={styles.labelShimmer} />
        <ShimmerLine className={styles.descriptionShimmer} />
      </div>
    </div>
  );
}

export default function SSHConfig({
  entityTypeIdentifier,
  entityType,
  hideRequestSSHAccessComponent = false,
  showNeedHelp = false,
  onNeedHelpClick = () => {},
  children,
  className,
  databaseHost,
  isEditing = false
}: SSHConfigProps) {
  const {
    peering,
    peeringLoading,
    peeringOffered,
    peeringAvailable,
    usePeering,
    tunnel,
    hostRefreshed,
    useManualSSH,
    onSelectHost,
    onTogglePeering,
    onToggleManualSSH
  } = useSSHConnectionMode({ entityType, isEditing });

  return (
    // `w-100` because the host form can lay its children out in a flex row (a
    // MUI Grid container's non-item children shrink to fit): without it this
    // block takes only the width of its widest toggle and the next control —
    // "Use SSL" on the SQL and MySQL/MariaDB forms — is pulled up alongside
    // "Use VPC Peering" instead of sitting on its own row.
    <div className={clsx('w-100', className)}>
      {/* Tells the customer to open their database to Hevo's public bastion IPs.
          Correct for a direct connection, wrong under either tunnel mode. */}
      {!peeringLoading && !usePeering && !useManualSSH && !hideRequestSSHAccessComponent ? (
        <Grid container spacing={2}>
          <Grid item xs={12} md={12}>
            <RequestSSHAccess entityTypeIdentifier={entityTypeIdentifier} />
          </Grid>
        </Grid>
      ) : null}

      {peeringLoading ? (
        <Grid container className='mb-4'>
          <Grid item xs={12} md={12}>
            {/* One per toggle row. Until the spec answers we cannot say which
                mode a saved config is in — `useSSH` is true for both, and the
                peering half only resolves once the spec lands — so rendering
                either switch would show a state we are about to contradict. */}
            <ToggleRowShimmer dataId='use-peering-shimmer' />

            <div className='mt-5'>
              <ToggleRowShimmer dataId='use-ssh-shimmer' />
            </div>
          </Grid>
        </Grid>
      ) : null}

      {!peeringLoading && peeringOffered ? (
        <Grid container>
          <Grid item xs={12} md={12}>
            <HdSwitchWrapper
              id='usePeering'
              dataId='use-peering'
              label='Use VPC Peering'
              disabled={!peeringAvailable}
              switchProps={{ name: 'usePeering', checked: usePeering }}
              onChange={event => onTogglePeering(event.target.checked)}
            >
              <Typography component='label' htmlFor='usePeering' className='mt-1'>
                <span>
                  {peeringAvailable
                    ? 'Reach this database over the established VPC peering connection.'
                    : 'Accept the peering connection in your AWS account to use it here.'}
                </span>
              </Typography>
            </HdSwitchWrapper>
          </Grid>
        </Grid>
      ) : null}

      {usePeering && peering ? (
        <VpcPeeringPanel
          peering={peering}
          tunnel={tunnel}
          onSelectHost={onSelectHost}
          databaseHost={databaseHost}
          hostRefreshed={hostRefreshed}
        />
      ) : null}

      {!peeringLoading ? (
      <Grid container className={peeringOffered ? 'mt-5' : undefined}>
        <Grid item xs={12} md={12}>
          {/* The two modes are mutually exclusive, and either switch can be the
              one you reach for: turning this on drops peering, and turning
              peering on drops this. Both stay enabled so neither is a dead
              control the customer has to back out of the other one to use. */}
          <HdSwitchWrapper
            id='useSSH'
            dataId='use-ssh'
            label='Connect Through SSH'
            switchProps={{ name: 'useSSH', checked: useManualSSH }}
            onChange={event => onToggleManualSSH(event.target.checked)}
          >
            <Typography component='label' htmlFor='useSSH' className='mt-1'>
              <span>
                Connect securely through an SSH tunnel if your database is not
                publicly accessible.
                &nbsp;
                {showNeedHelp && (
                  <HdLink tag='button' onClick={onNeedHelpClick} dataId='ssh'>
                    Need Help?
                  </HdLink>
                )}
              </span>
            </Typography>
          </HdSwitchWrapper>
        </Grid>
      </Grid>
      ) : null}

      {peeringLoading || usePeering ? null : children}
    </div>
  );
}
